import React, { useState, useEffect, useRef, lazy, Suspense, memo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ReactGA from 'react-ga4';

// Component Imports
import TopBar from './components/TopBar';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Error404 from './components/404Page';
import ChatWidget from './components/ChatWidget';
import CallNow from './components/CallNow';
import EmbeddableChat from './components/Wizard/EmbeddableChat';
import BotManagementDashboard from './components/Wizard/BotManagementDashboard';
import PrivateRoute from './components/PrivateRoute';
import PaymentPage from './components/Payment';
import SuccessPage from './components/Wizard/SuccessPage';
import WebDevelopmentPage from './pages/Services/Webdev';
import Wizard from './components/Wizard/Wizard';

// Context Imports
import { FirebaseProvider } from './context/FirebaseContext';
import { WorkflowProvider } from './context/WorkflowContext';
import { WizardProvider } from './components/Wizard/WizardContext';

// Lazy-loaded Pages
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services/Services'));
const Worskhops = lazy(() => import('./pages/Services/Workshops'));
const AiConsultancy = lazy(() => import('./pages/Services/Ai-Consultancy'));
const Blog = lazy(() => import('./pages/Resources/Blog'));
const Contact = lazy(() => import('./pages/Contact'));
const Partners = lazy(() => import('./pages/Community/Partners'));
const Careers = lazy(() => import('./pages/Community/Careers'));
const FAQPage = lazy(() => import('./pages/FAQs'));
const CommunityService = lazy(() => import('./pages/Community/community-service'));
const BecomeAPartner = lazy(() => import('./pages/Community/Become-A-Partner'));
const GetYourVolunteerHours = lazy(() => import('./pages/Resources/Get-Your-Voulenteer-Hours'));
const Content = lazy(() => import('./pages/Resources/Content'));
const Terms = lazy(() => import('./pages/Resources/Terms'));
const SiteMap = lazy(() => import('./pages/Resources/Site-Map'));
const VuxoChat = lazy(() => import('./pages/Products/Vuxo-chat'));
const VuxoConnect = lazy(() => import('./pages/Products/Vuxo-voice'));
const AppDevelopmentPage = lazy(() => import('./pages/Services/Appdev'));
const AiIntegration = lazy(() => import('./pages/Unlisted/Ai-Integration'));
const VuxoAI = lazy(() => import('./pages/Unlisted/Vuxoai'));
const LoginPage = lazy(() => import('./pages/Workflow/LoginPage'));
const PlansPage = lazy(() => import('./pages/Pricing'));
const PersonalizeStep1 = lazy(() => import('./pages/Workflow/PersonalizeStep1'));
const PersonalizeStep2 = lazy(() => import('./pages/Workflow/PersonalizeStep2'));
const PersonalizeStep3 = lazy(() => import('./pages/Workflow/PersonalizeStep3'));
const PersonalizeStep4 = lazy(() => import('./pages/Workflow/PersonalizeStep4'));
const PersonalizeStep5 = lazy(() => import('./pages/Workflow/PersonalizeStep5'));
const WorkflowCompletePage = lazy(() => import('./pages/Workflow/WorkflowCompletePage'));
const ChatInterface = lazy(() => import('./components/Wizard/ChatInterface'));
const PublicChatInterface = lazy(() => import('./components/Wizard/PublicChatInterface'));
const Admin = lazy(() => import('./pages/Unlisted/Admin'));

// Loader Styles
const LoaderStyles = `
  .loader-container {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #1f2937;
    z-index: 9999;
    transition: opacity 0.3s ease-out;
  }
  .loader-fallback {
    color: #ffffff;
    font-size: 1rem;
    font-weight: bold;
    text-transform: uppercase;
    text-shadow: 0 0 0.1rem rgba(255, 255, 255, 0.5);
  }
`;

// Loader Components
const Loaders = {
  Dot: memo(() => (
    <div className="loader-container" aria-label="Loading animation with rotating dots">
      <div className="dot-loader">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="dot-loader__dot" />
        ))}
        <div className="dot-loader__text">Loading...</div>
      </div>
      <style>{`
        ${LoaderStyles}
        .dot-loader {
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
          width: 12rem;
          height: 12rem;
          color: #ffffff;
          transform: rotateX(30deg) rotateZ(45deg);
          box-shadow: 1.5rem 0 1.5rem rgba(0, 0, 0, 0.3) inset, -1.5rem 0 1.5rem rgba(255, 255, 255, 0.1) inset;
          border-radius: 50%;
          animation: glow 3s ease-in-out infinite;
        }
        .dot-loader__dot {
          position: absolute;
          width: 1.2rem;
          height: 1.2rem;
          top: calc(50% - 0.6rem);
          left: calc(50% - 0.6rem);
          border-radius: 50%;
          box-shadow: 0.1rem 0.1rem 0 0.1rem #1f2937, 0.2rem 0 0.2rem rgba(0, 0, 0, 0.4);
          animation: dotShadow 1.8s ease-in-out infinite;
        }
        .dot-loader__dot:before {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background-color: #4b5563;
          box-shadow: 0.05rem 0 0.1rem rgba(255, 255, 255, 0.2) inset;
          animation: dotPush 1.8s ease-in-out infinite;
          z-index: 1;
        }
        .dot-loader__dot:after {
          content: '';
          position: absolute;
          width: 100%;
          height: 2.4rem;
          bottom: 0;
          border-radius: 0.6rem;
          background-color: #6b7280;
          box-shadow: 0.1rem 0.2rem 0.2rem rgba(255, 255, 255, 0.3) inset, 0 -0.3rem 0.2rem #1f2937 inset;
          clip-path: polygon(0 70%, 100% 70%, 100% 100%, 0 100%);
          transform: rotate(-45deg);
          transform-origin: 50% 1.8rem;
          animation: dotPushAfter 1.8s ease-in-out infinite;
        }
        ${[...Array(10)]
          .map(
            (_, i) => `
          .dot-loader__dot:nth-child(${i + 1}) {
            transform: rotate(${i * -36}deg) translateX(4.5rem) rotate(${i * 36}deg);
            z-index: ${6 - Math.abs(i - 5)};
          }
          .dot-loader__dot:nth-child(${i + 1}),
          .dot-loader__dot:nth-child(${i + 1}):before,
          .dot-loader__dot:nth-child(${i + 1}):after {
            animation-delay: ${i * -0.18}s;
          }
        `
          )
          .join('')}
        .dot-loader__text {
          font-size: 0.8rem;
          font-weight: bold;
          position: relative;
          transform: rotateZ(-45deg);
          text-transform: uppercase;
          text-shadow: 0 0 0.1rem rgba(255, 255, 255, 0.5);
        }
        @keyframes dotShadow {
          0%, 50%, 100% { box-shadow: 0.1rem 0.1rem 0 0.1rem #1f2937, 0.2rem 0 0.2rem rgba(0, 0, 0, 0.4); }
          25% { box-shadow: 0.1rem 0.1rem 0 0.1rem #1f2937, 0.6rem 0 0.6rem rgba(0, 0, 0, 0.5); }
        }
        @keyframes dotPush {
          0%, 50%, 100% { background-color: #4b5563; transform: translate(0, 0); }
          25% { background-color: #9333ea; transform: translate(-60%, -60%); }
        }
        @keyframes dotPushAfter {
          0%, 50%, 100% { background-color: #6b7280; clip-path: polygon(0 70%, 100% 70%, 100% 100%, 0 100%); }
          25% { background-color: #7e22ce; clip-path: polygon(0 30%, 100% 30%, 100% 100%, 0 100%); }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 1.5rem 0 1.5rem rgba(0, 0, 0, 0.3) inset, -1.5rem 0 1.5rem rgba(255, 255, 255, 0.1) inset; }
          50% { box-shadow: 1.5rem 0 1.5rem rgba(0, 0, 0, 0.3) inset, -1.5rem 0 1.5rem rgba(147, 51, 234, 0.2) inset; }
        }
      `}</style>
    </div>
  )),

  Cube: memo(() => (
    <div className="loader-container" aria-label="Loading animation with rotating 3D cube">
      <div className="cube-loader">
        <div className="cube">
          <div className="face front">{[...Array(9)].map((_, i) => <div key={i} className="sticker" />)}</div>
          <div className="face back">{[...Array(9)].map((_, i) => <div key={i} className="sticker" />)}</div>
          <div className="face right">{[...Array(9)].map((_, i) => <div key={i} className="sticker" />)}</div>
          <div className="face left">{[...Array(9)].map((_, i) => <div key={i} className="sticker" />)}</div>
          <div className="face top">{[...Array(9)].map((_, i) => <div key={i} className="sticker" />)}</div>
          <div className="face bottom">{[...Array(9)].map((_, i) => <div key={i} className="sticker" />)}</div>
        </div>
      </div>
      <style>{`
        ${LoaderStyles}
        .cube-loader {
          position: relative;
          width: 160px;
          height: 160px;
          transform-style: preserve-3d;
          animation: rotate-cube 6.5s cubic-bezier(0.76, 0, 0.24, 1) infinite;
          transition: transform 0.6s cubic-bezier(0.68, -0.55, 0.27, 1.55);
        }
        .cube-loader:hover {
          animation-play-state: paused;
          transform: scale(1.1);
        }
        .cube {
          position: absolute;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          transform: rotateX(35.264deg) rotateY(45deg);
        }
        .face {
          position: absolute;
          width: 160px;
          height: 160px;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          grid-gap: 5px;
          padding: 5px;
          box-sizing: border-box;
          background: rgba(31, 41, 55, 0.9);
          border: 2px solid rgba(255, 255, 255, 0.1);
          transition: box-shadow 0.4s cubic-bezier(0.4, 0, 0.6, 1);
        }
        .face:hover {
          box-shadow: 0 0 20px rgba(147, 51, 234, 0.3);
        }
        .sticker {
          background: #fff;
          border-radius: 6px;
          animation: sticker-bounce 1.4s cubic-bezier(0.68, -0.55, 0.27, 1.55) infinite;
        }
        .front { transform: translateZ(80px); }
        .back { transform: translateZ(-80px) rotateY(180deg); }
        .right { transform: translateX(80px) rotateY(90deg); }
        .left { transform: translateX(-80px) rotateY(-90deg); }
        .top { transform: translateY(-80px) rotateX(90deg); }
        .bottom { transform: translateY(80px) rotateX(-90deg); }
        .front .sticker { background: linear-gradient(135deg, #9333ea, #c084fc); }
        .back .sticker { background: linear-gradient(135deg, #4b5563, #9ca3af); }
        .right .sticker { background: linear-gradient(135deg, #7e22ce, #a855f7); }
        .left .sticker { background: linear-gradient(135deg, #6b7280, #d1d5db); }
        .top .sticker { background: linear-gradient(135deg, #3b0764, #6b21a8); }
        .bottom .sticker { background: linear-gradient(135deg, #d946ef, #f3e8ff); }
        @keyframes rotate-cube {
          0% { transform: rotateX(35.264deg) rotateY(45deg) rotateZ(0deg); }
          25% { transform: rotateX(35.264deg) rotateY(135deg) rotateZ(0deg); }
          50% { transform: rotateX(35.264deg) rotateY(225deg) rotateZ(0deg); }
          75% { transform: rotateX(35.264deg) rotateY(315deg) rotateZ(0deg); }
          100% { transform: rotateX(35.264deg) rotateY(405deg) rotateZ(0deg); }
        }
        @keyframes sticker-bounce {
          0% { transform: translateY(0) scale(1); }
          50% { transform: translateY(0) scale(1.03); }
          100% { transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  )),
};

// Loader Component
const Loader = () => {
  const [loaderType] = useState(() => {
    const seed = Date.now();
    const random = (seed % 100) / 100;
    return random < 0.5 ? 'dot' : 'cube';
  });

  const LoaderComponent = Loaders[loaderType.charAt(0).toUpperCase() + loaderType.slice(1)] || (() => <div className="loader-fallback">Loading...</div>);
  return <LoaderComponent />;
};

// Suspense Wrapper
const SuspenseWithLoading = ({ children }) => (
  <Suspense fallback={<Loader />}>{children}</Suspense>
);

// Analytics Tracker
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.initialize('G-N5VKSGJCJJ');
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location]);

  return null;
};

// Route Configuration
const routes = [
  // Public Routes
  { path: '/', element: <Home /> },
  { path: '/about', element: <About /> },
  { path: '/services', element: <Services /> },
  { path: '/contact', element: <Contact /> },
  { path: '/blogs', element: <Blog /> },
  { path: '/workshops', element: <Worskhops /> },
  { path: '/ai-consultancy', element: <AiConsultancy /> },
  { path: '/partners', element: <Partners /> },
  { path: '/careers', element: <Careers /> },
  { path: '/faqs', element: <FAQPage /> },
  { path: '/community-service', element: <CommunityService /> },
  { path: '/become-a-partner', element: <BecomeAPartner /> },
  { path: '/volunteer-hours', element: <GetYourVolunteerHours /> },
  { path: '/content', element: <Content /> },
  { path: '/terms', element: <Terms /> },
  { path: '/sitemaps', element: <SiteMap /> },
  { path: '/vuxo-chat', element: <VuxoChat /> },
  { path: '/vuxo-voice', element: <VuxoConnect /> },
  { path: '/vuxo-ai', element: <VuxoAI /> },
  { path: '/app-development', element: <AppDevelopmentPage /> },
  { path: '/website-development', element: <WebDevelopmentPage /> },
  { path: '/ai-admin', element: <Admin /> },
  // Workflow Routes
  { path: '/login', element: <LoginPage /> },
  { path: '/plans', element: <PlansPage /> },
  { path: '/payment', element: <PaymentPage /> },
  { path: '/workflow/personalize/step1', element: <PersonalizeStep1 /> },
  { path: '/workflow/personalize/step2', element: <PersonalizeStep2 /> },
  { path: '/workflow/personalize/step3', element: <PersonalizeStep3 /> },
  { path: '/workflow/personalize/step4', element: <PersonalizeStep4 /> },
  { path: '/workflow/personalize/step5', element: <PersonalizeStep5 /> },
  { path: '/workflow/complete', element: <WorkflowCompletePage /> },
  { path: '/payment/success', element: <SuccessPage /> },
  { path: '/embed/:identifier', element: <EmbeddableChat /> },
  { path: '/wizard', element: <Wizard /> },
  // Protected Routes
  { path: '/workflow/chat', element: <PrivateRoute><ChatInterface /></PrivateRoute> },
  { path: '/dashboard/bots', element: <PrivateRoute><BotManagementDashboard /></PrivateRoute> },
  // Portfolio and Redirects
  { path: '/portfolio/items/ai-integration', element: <AiIntegration /> },
  { path: '/portfolio/items', element: <Navigate to="/ai-integration" replace /> },
  { path: '/public-chat/:botId', element: <PublicChatInterface /> },
  // Fallback Route
  { path: '*', element: <Error404 /> },
];

// Layout Configuration
// Add or remove paths to control visibility of TopBar, Navbar, Footer, and ChatWidget
const layoutConfig = {
  // Routes where only Footer is hidden
  hideFooterOnly: [
    '/wizard',
    '/dashboard/bots',
    '/login',
    '/contact',
    '/terms',
    '/workflow/chat',
  ],
  // Routes where TopBar, Navbar, Footer, and ChatWidget are all hidden
  hideAll: [
    '/embed/:identifier',
    '/public-chat/:botId', // Ensures clean iframe embedding
  ],
  // Routes where only ChatWidget is hidden
  hideChatWidgetOnly: [
    '/wizard',
    '/dashboard/bots',
  ],
};

// Layout Config Hook
const useLayoutConfig = () => {
  const location = useLocation();
  const pathname = location.pathname;

  // Helper function to match routes, including those with dynamic parameters
  const matchPath = (path) => 
    path.includes(':') 
      ? new RegExp(`^${path.replace(/:[^/]+/, '[^/]+')}$`).test(pathname) 
      : path === pathname;

  const hideAll = layoutConfig.hideAll.some(matchPath);
  const showTopBar = !hideAll;
  const showNavbar = !hideAll;
  const showFooter = !hideAll && !layoutConfig.hideFooterOnly.some(matchPath);
  const showChatWidget = !hideAll && !layoutConfig.hideChatWidgetOnly.some(matchPath);

  return { showTopBar, showNavbar, showFooter, showChatWidget };
};

// Layout Component
const Layout = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { showTopBar, showNavbar, showFooter, showChatWidget } = useLayoutConfig();

  // Handle responsive layout and scroll behavior
  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth <= 768);
      }, 50);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Calculate padding based on visible components
  const topBarHeight = showTopBar ? (isMobile ? 35 : 45) : 0;
  const navbarHeight = showNavbar ? (isMobile ? 64 : 80) : 0;
  const mainPaddingTop = topBarHeight + navbarHeight;

  return (
    <div className="layout flex flex-col min-h-screen bg-gray-900">
      <header className="header fixed top-0 left-0 right-0 z-50">
        {showTopBar && <TopBar />}
        {showNavbar && <Navbar isMobile={isMobile} />}
      </header>
      <main className="flex-grow" style={{ paddingTop: `${mainPaddingTop}px` }}>
        <WorkflowProvider>
          <WizardProvider>
            <SuspenseWithLoading>
              <Routes>
                {routes.map(({ path, element }) => (
                  <Route key={path} path={path} element={element} />
                ))}
              </Routes>
            </SuspenseWithLoading>
          </WizardProvider>
        </WorkflowProvider>
      </main>
      {showFooter && (
        <footer className="footer bg-gray-900">
          <Footer />
        </footer>
      )}
      {showChatWidget && (
        <>
          <ChatWidget />
          <CallNow />
        </>
      )}
    </div>
  );
};

// Main App Component
export default function App() {
  return (
    <HelmetProvider>
      <FirebaseProvider>
        <Router>
          <ScrollToTop />
          <AnalyticsTracker />
          <Layout />
        </Router>
      </FirebaseProvider>
    </HelmetProvider>
  );
}